/**
 * useDashboardData — owns the LeagueDashboard's entire data + realtime layer.
 *
 * Extracted from `LeagueDashboard.jsx` (iter63z'''''''') to peel ~250 lines
 * of state, callbacks, and side-effects out of what had grown to a 1,200-line
 * page component. Behaviour is preserved verbatim — no logic changes.
 *
 * Owns:
 *   - league payload (`data`), leaderboard, participants (via `data`)
 *   - current user (`me`) + spectator flag
 *   - movers, recent changes, season Rivalry Standing pill
 *   - Smack Stream ping counter + unread @-mention count
 *   - Tournament switcher state (`viewTournamentId`, refresh key, count)
 *   - WebSocket subscription + 45s polling fallback (with auto-stop on 5 failures)
 *   - Throttled `load()` callback (4s soft-throttle, `force: true` to bypass)
 *
 * Returns a flat shape the page consumes — keeps the refactor diff at the
 * call site small.
 *
 * ─── JSDoc typedefs ──────────────────────────────────────────────────────
 *
 * @typedef {Object} SessionData
 * @property {string} league_id     — the league this session is bound to
 * @property {string} participant_id
 * @property {string} username
 * @property {boolean} is_admin
 *
 * @typedef {Object} LeagueDoc
 * @property {string} id
 * @property {string} name
 * @property {string} invite_code
 * @property {string=} active_tournament_id
 * @property {boolean=} smack_stream_enabled
 * @property {boolean=} spectator_enabled
 *
 * @typedef {Object} TournamentDoc
 * @property {string} id
 * @property {string} name
 * @property {string} template       — e.g. "world_cup", "march_madness", "custom"
 * @property {string} format         — e.g. "group_knockout", "round_robin"
 * @property {Array<Object>} teams
 * @property {Array<{key:string,label:string}>} stages
 * @property {string} current_stage
 * @property {string} status         — "draft" | "active" | "completed" | etc.
 *
 * @typedef {Object} ParticipantDoc
 * @property {string} id
 * @property {string} league_id
 * @property {string} username
 * @property {boolean=} is_admin
 * @property {boolean=} is_co_admin
 * @property {boolean=} is_player
 * @property {string=} email
 * @property {Array<string>=} assigned_team_ids
 *
 * @typedef {Object} LeaderboardRow
 * @property {string} participant_id
 * @property {string} username
 * @property {number} rank
 * @property {number} points
 *
 * @typedef {Object} DashboardPayload
 * @property {LeagueDoc} league
 * @property {TournamentDoc=} tournament
 * @property {Array<ParticipantDoc>} participants
 * @property {Array<LeaderboardRow>} leaderboard
 * @property {Object=} draft
 * @property {boolean=} is_spectator_view
 *
 * @typedef {Object} SeasonStandingPill
 * @property {number} rank
 * @property {number} points
 * @property {number} total_players
 *
 * @typedef {Object} LeagueChangeEntry
 * @property {string} reason
 * @property {string} created_at
 * @property {Object=} payload
 *
 * @typedef {Object} UseDashboardDataReturn
 * @property {SessionData|null} sessionData      — raw localStorage session
 * @property {boolean} hasSession                — true if sessionData matches the current leagueId
 * @property {DashboardPayload|null} data        — full league payload (null until first load)
 * @property {Array<LeaderboardRow>} board       — `data.leaderboard` mirror, kept as a separate setter target
 * @property {ParticipantDoc|null} me            — the signed-in participant, resolved from `data.participants`
 * @property {(p: ParticipantDoc) => void} setMe — used after AddEmailModal saves a recovery email
 * @property {boolean} isSpectator               — true when viewing a read-only spectator payload
 * @property {Object|null} roundMovers           — per-round leaderboard movement payload from /api/movers
 * @property {Array<LeagueChangeEntry>} recentChanges — last 5 league-change entries for the Messages card
 * @property {(rows: Array<LeagueChangeEntry>) => void} setRecentChanges
 * @property {SeasonStandingPill|null} seasonStanding — { rank, points, total_players } for the header pill (multi-tournament leagues only)
 * @property {boolean} wsConnected               — true once the WS sends its first `connected` event
 * @property {number} smackPing                  — increments on smack_* WS events; SmackStreamCard refetches on change
 * @property {number} unreadMentions             — count of unread @-mentions for the header chip
 * @property {(n: number | ((prev:number)=>number)) => void} setUnreadMentions — bumped live by WS and cleared by chip click
 * @property {string|null} viewTournamentId      — local override; null = use league.active_tournament_id
 * @property {(tid: string|null) => void} setViewTournamentId
 * @property {number} switcherRefreshKey         — bumped on tournament_created / completed / reopened so TournamentSwitcher re-fetches its list
 * @property {number} tournamentCount            — cached count; gates a few multi-tournament-only affordances (was the Crown Race header link before the iter87.199.5 UI deprecation)
 * @property {(n: number) => void} setTournamentCount
 * @property {(opts?: { force?: boolean }) => Promise<void>} load — throttled (4s) primary loader; pass `{force:true}` to bypass
 * @property {() => void} resetLoadThrottle      — escape hatch for TournamentSwitcher onChange — clears the 4s throttle so the very next load() goes through
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, session, openLeagueSocket } from "@/lib/api";
import { getStoredViewTournament } from "@/components/dashboard/TournamentSwitcher";

/**
 * @param {string} leagueId — current `:leagueId` URL param
 * @returns {UseDashboardDataReturn}
 */
export default function useDashboardData(leagueId) {
    const navigate = useNavigate();
    const sessionData = session.load();
    const hasSession = !!(sessionData && sessionData.league_id === leagueId);

    // ─── core payload state ────────────────────────────────────────────
    const [data, setData] = useState(null);
    const [board, setBoard] = useState([]);
    const [me, setMe] = useState(null);
    const [isSpectator, setIsSpectator] = useState(false);
    const [roundMovers, setRoundMovers] = useState(null);
    const [recentChanges, setRecentChanges] = useState([]);
    const [seasonStanding, setSeasonStanding] = useState(null);
    const [wsConnected, setWsConnected] = useState(false);

    // ─── smack stream live state ──────────────────────────────────────
    const [smackPing, setSmackPing] = useState(0);
    const [unreadMentions, setUnreadMentions] = useState(0);

    // ─── tournament switcher state ────────────────────────────────────
    const [viewTournamentId, setViewTournamentId] = useState(() => getStoredViewTournament(leagueId));
    const [switcherRefreshKey, setSwitcherRefreshKey] = useState(0);
    const [tournamentCount, setTournamentCount] = useState(1);

    // ─── internal refs (load throttling, toast dedupe) ────────────────
    const loadFailCountRef = useRef(0);
    const errorToastShownRef = useRef(false);
    const lastLoadAtRef = useRef(0);
    const errorToastIdRef = useRef(null);
    // iter63z''' — Fires the "you have unread mentions" load-toast only ONCE
    // per dashboard mount. Polling refreshes shouldn't re-toast every 45s.
    const unreadToastShownRef = useRef(false);
    // iter86.76 — Monotonic request counter: a SLOW poll response landing
    // AFTER a newer load (e.g. the post-pick-submit refresh) must not
    // revert `me.assigned_team_ids` to pre-submit data ("board cleared").
    const loadSeqRef = useRef(0);

    // ─── load callback (throttled, idempotent) ─────────────────────────
    const load = useCallback(async (opts = {}) => {
        // Throttle: skip if called less than 4s after the previous call.
        // `opts.force=true` bypasses the throttle (used after switching tournament view).
        const now = Date.now();
        if (!opts.force && now - lastLoadAtRef.current < 4000) return;
        lastLoadAtRef.current = now;
        try {
            // Single endpoint now returns league + tournament + matches + leaderboard.
            // iter45 (M3) — pass the user's locally-overridden tournament view
            // (if any) so the dashboard renders the chosen tournament instead
            // of the league default.
            const seq = ++loadSeqRef.current;
            const d = await api.getLeague(leagueId, { spectator: !hasSession, tournamentId: viewTournamentId });
            if (seq !== loadSeqRef.current) return; // stale response — a newer load superseded it
            setData(d);
            setIsSpectator(!!d.is_spectator_view);
            setBoard(d.leaderboard || []);
            if (d.tournament) {
                try {
                    // iter63z'''' — Pass the currently-viewed tournament_id so
                    // multi-tournament leagues get per-tournament movers.
                    // Falls back to the league's active tournament on the
                    // backend when omitted (single-tournament legacy).
                    const rm = await api.roundMovers(leagueId, d.tournament.id);
                    setRoundMovers(rm);
                } catch (e) {
                    console.warn("movers fetch failed", e?.response?.status);
                }
            }
            if (sessionData) {
                const myP = d.participants.find(p => p.id === sessionData.participant_id);
                if (myP) setMe(myP);
            }
            // iter87.199.6 — Season Standings pill fetch removed with
            // the Crown Race excise. Header pill retired, endpoint
            // deleted. The Title Race replacement (Trophy Tokens
            // Rivalry-level roll-up) will re-introduce a fetch here
            // when built.
            // Recent changes feed (best-effort; failure doesn't break the dashboard)
            try {
                const ch = await api.listLeagueChanges(leagueId, 5);
                setRecentChanges(ch);
            } catch { /* ignore */ }
            // iter63z''' — Unread @-mention count (LEAGUE-LEVEL as of iter63z''''').
            // One surfaced toast per dashboard load when the count is > 0 so
            // people who weren't online when they got tagged still see it.
            // We only fire the toast on the FIRST load per mount — gated by
            // a ref so subsequent polling refreshes don't re-toast.
            if (sessionData?.participant_id) {
                try {
                    const um = await api.getSmackUnread(leagueId);
                    const n = Number(um?.count || 0);
                    setUnreadMentions(n);
                    if (n > 0 && !unreadToastShownRef.current) {
                        unreadToastShownRef.current = true;
                        toast.message(
                            `📬 You have ${n} unread mention${n === 1 ? "" : "s"} in Smack`,
                            {
                                description: "Click the @ chip in the header to jump there.",
                                duration: 5000,
                                position: "top-center",
                            }
                        );
                    }
                } catch { /* non-fatal */ }
            }
            // Success: clear any visible error toast + reset counters
            loadFailCountRef.current = 0;
            errorToastShownRef.current = false;
            if (errorToastIdRef.current !== null) {
                toast.dismiss(errorToastIdRef.current);
                errorToastIdRef.current = null;
            }
        } catch (err) {
            const status = err.response?.status;
            console.error("LeagueDashboard load failed", { status, url: err.config?.url, message: err.message });

            if (status === 404) {
                toast.error("This league no longer exists — please join again");
                session.clear();
                navigate("/");
                return;
            }
            loadFailCountRef.current += 1;
            // Suppress transient blips. Only toast after 3 CONSECUTIVE failures.
            // Replace any prior toast instead of stacking new ones.
            if (loadFailCountRef.current >= 3 && !errorToastShownRef.current) {
                errorToastIdRef.current = toast.error(
                    `Connection unstable (${status || "network"}) — still retrying...`,
                    { duration: Infinity }
                );
                errorToastShownRef.current = true;
            }
        }
        // sessionData intentionally excluded (only its stable participant_id
        // is depended on) — `session.load()` returns a new object every
        // render, so depending on the whole object would recreate `load` on
        // every render and thrash the WS subscription that depends on it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leagueId, sessionData?.participant_id, navigate, viewTournamentId, hasSession]);

    // ─── WebSocket subscription + polling fallback ─────────────────────
    useEffect(() => {
        // No session AND league hasn't opted into spectator mode → bounce to landing.
        // If the user has no session BUT the league allows spectators, we let the load()
        // call proceed; the spectator-trimmed payload tells us whether to render or redirect.
        if (!hasSession) {
            // We need to peek at the league before deciding. Best-effort: kick off load(),
            // then react if the league doesn't allow spectators.
            (async () => {
                try {
                    const peek = await api.getLeague(leagueId, { spectator: true });
                    if (!peek.is_spectator_view) {
                        toast.error("You're not in this rivalry. Use an invite code to join.");
                        navigate("/");
                        return;
                    }
                    setData(peek);
                    setIsSpectator(true);
                    setBoard(peek.leaderboard || []);
                } catch (err) {
                    toast.error("League not found");
                    navigate("/");
                }
            })();
            return;
        }
        load();
        // WebSocket for real-time updates, polling fallback
        const sock = openLeagueSocket(leagueId, (msg) => {
            // Smack Stream events — bump the ping so SmackStreamCard refetches.
            if (msg.event === "smack_added" || msg.event === "smack_deleted" || msg.event === "smack_stream_toggled") {
                setSmackPing((n) => n + 1);
            }
            // iter63z'' — Per-mention WS event. When someone @-tags the
            // current user we surface a toast so they know to check Smack
            // even if they're on another tab of the dashboard.
            if (msg.event === "smack_mention") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.mentioned_participant_id === myId) {
                    setSmackPing((n) => n + 1);
                    // iter63z''' — Bump the unread badge live so the header
                    // chip jumps in real-time without waiting for a refresh.
                    setUnreadMentions((n) => n + 1);
                    toast.message(
                        `@ ${p.author_username || "Someone"} tagged you in Smack`,
                        { description: p.snippet || "", duration: 6000 }
                    );
                }
            }
            // iter43e — Reaction toast notification. Show only when someone
            // ELSE reacted to YOUR message (and only on the "added" action;
            // un-reacting is silent so people can fix typos without spam).
            if (msg.event === "smack_reaction_changed") {
                const p = msg.payload || {};
                setSmackPing((n) => n + 1);
                const myId = sessionData?.participant_id;
                if (
                    myId
                    && p.action === "added"
                    && p.recipient_id === myId
                    && p.actor_id !== myId
                ) {
                    toast.message(
                        `${p.emoji || "👍"} ${p.actor_username || "Someone"} reacted to your smack`,
                        { duration: 4500 }
                    );
                }
            }
            if (msg.event === "connected") setWsConnected(true);
            if (msg.event === "match_recorded") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                const myDelta = myId ? (p.rank_deltas?.[myId] ?? 0) : 0;
                const scoreStr = `${p.score_a}-${p.score_b}`;
                const titleEmoji = p.is_draw ? "🤝" : "⚽";
                let detail = "";
                if (myDelta > 0) detail = `You moved ↑${myDelta} rank${myDelta > 1 ? "s" : ""}`;
                else if (myDelta < 0) detail = `You dropped ↓${Math.abs(myDelta)} rank${Math.abs(myDelta) > 1 ? "s" : ""}`;
                toast.message(
                    `${titleEmoji} ${p.team_a_flag || ""} ${p.team_a_name || "Team A"} ${scoreStr} ${p.team_b_flag || ""} ${p.team_b_name || "Team B"}`,
                    { description: detail || `Stage: ${p.stage?.toUpperCase()}`, duration: 6000 }
                );
                load();
            }
            if (msg.event === "draft_started") { toast.success("Draft started!"); load(); }
            if (msg.event === "draft_pick") load();
            // iter87.278.2 (user-requested — "the player has to get
            // notified that the drafted team drops out") — fired by
            // `season_pickem.reconcile_dropped_pot_picks` the instant a
            // LOCKED pick's team misses the real playoff field. Only
            // toast the affected player, not the whole league.
            if (msg.event === "pot_pick_dropped") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const names = (p.team_names || []).join(", ") || "A team you picked";
                    toast.error(`${names} missed the playoff field`, {
                        description: p.power_pick_cleared
                            ? "Your Power Pick was cleared too — pick a new one, and swap in a replacement pick when ready."
                            : "Swap in a replacement pick when ready.",
                        duration: 8000,
                    });
                    load();
                }
            }
            if (msg.event === "pot_pick_dropped_replaced") load();
            // iter87.363 (user-reported — "what happens when a
            // selection drops out of the top 8?") — fired by
            // `nwslplayoffs_engine.reconcile_dropped_nwsl_picks` the
            // instant a Champions Pick / Dark Horse Pick team falls
            // out of the live top-8 read. Only toast the affected
            // player.
            if (msg.event === "nwsl_pick_dropped") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const names = (p.picks_cleared || []).join(" + ") || "A pick you made";
                    toast.error(`${names} cleared — that team dropped out of the top 8`, {
                        description: "Pick a new one before Round 1 kicks off.",
                        duration: 8000,
                    });
                    load();
                }
            }
            // iter87.442 (user-requested — "toast + auto-clear flow
            // for when a picked Champion/Dark Horse team drops out of
            // the live rankings") — the auto-clear itself already
            // existed (`nflplayoffs_engine.reconcile_dropped_nfl_
            // picks`, fired the instant `missed_playoffs` flips True
            // or a Dark Horse's seed moves into the bye range); this
            // was just missing its toast, unlike NWSL/pot above.
            // testing-agent-flagged: names the dropped team(s) too
            // (`team_names`), so a multi-tournament league knows
            // which pick/team was affected without guessing.
            if (msg.event === "nfl_pick_dropped") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const names = (p.picks_cleared || []).join(" + ") || "A pick you made";
                    const teams = (p.team_names || []).join(", ");
                    toast.error(`${names} cleared${teams ? ` — ${teams} dropped out of the playoff field` : ""}`, {
                        description: "Pick a new one before Wild Card kickoff.",
                        duration: 8000,
                    });
                    load();
                }
            }
            // iter87.442 — same gap, CFB side (`cfbplayoffs_engine.
            // reconcile_dropped_cfb_picks`).
            if (msg.event === "cfb_pick_dropped") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const names = (p.picks_cleared || []).join(" + ") || "A pick you made";
                    const teams = (p.team_names || []).join(", ");
                    toast.error(`${names} cleared${teams ? ` — ${teams} dropped out of the playoff field` : ""}`, {
                        description: "Pick a new one before the First Round kicks off.",
                        duration: 8000,
                    });
                    load();
                }
            }
            // iter87.465.23 (user-reported — "confirm drops from the
            // playoffs and darkhorse as well as the playin... will
            // have a toast") — fired by `mls_playoffs_engine.
            // reconcile_dropped_mls_picks` (Champions/Dark Horse/
            // Play-In Bonus, and since iter87.530 also Round One
            // Series Predictor picks when a reseed swaps the teams
            // occupying a series slot out from under an existing
            // pick. Match Predictor picks target a fixed, already-
            // locked game and can't go stale the same way, so those
            // don't emit this event).
            if (msg.event === "mls_pick_dropped") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const cleared = p.picks_cleared || [];
                    const names = cleared.join(" + ") || "A pick you made";
                    // iter87.530 (testing-agent-reported) — a Series
                    // Predictor pick clears because reseeding swapped
                    // the teams in that matchup, not because a team
                    // "dropped out of the playoff field," and it locks
                    // at that series' Game 1, not Wild Card kickoff.
                    // Only reword when EVERY cleared label this pass is
                    // a Series Predictor one; a mixed pass (e.g. also a
                    // dropped Champions Pick) keeps the original copy,
                    // which is still accurate for that part.
                    const onlySeries = cleared.length > 0 && cleared.every((c) => c.startsWith("Series Predictor pick"));
                    if (onlySeries) {
                        toast.error(`${names} cleared — seeding changed and that matchup no longer has your pick`, {
                            description: "Pick a new one before that series' Game 1 kicks off.",
                            duration: 8000,
                        });
                    } else {
                        toast.error(`${names} cleared — your team dropped out of the playoff field`, {
                            description: "Pick a new one before Wild Card kickoff.",
                            duration: 8000,
                        });
                    }
                    load();
                }
            }
            // iter87.506 (user-reported — "confirm this includes toast
            // / notification if one of the team selected drops out")
            // — same gap as MLS/NFL/CFB above, UCL side
            // (`ucl_playoffs_engine.reconcile_dropped_ucl_picks`,
            // Champion + Dark Horse only). The auto-clear already
            // existed; this was just missing its toast.
            if (msg.event === "ucl_pick_dropped") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const names = (p.picks_cleared || []).join(" + ") || "A pick you made";
                    toast.error(`${names} cleared — your team dropped out of the field`, {
                        description: "Pick a new one before the Knockout Round Play-offs kick off.",
                        duration: 8000,
                    });
                    load();
                }
            }
            // iter87.279 (user-reported: "my Rangers Series Predictor
            // pick dropped off with no notification") — fired by
            // `mlb_engine.reconcile_dropped_mlb_series_picks` the
            // instant a projected Wild Card pick's team no longer
            // matches that series' live-seed matchup. Only toast the
            // affected player.
            if (msg.event === "mlb_series_pick_dropped") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const n = (p.series_ids || []).length;
                    toast.error(`Your Series Predictor pick${n > 1 ? "s" : ""} dropped out of the field`, {
                        description: "That team no longer holds the seed you picked it for — make a new pick.",
                        duration: 8000,
                    });
                    load();
                }
            }
            // iter87.298.2 (operator-corrected — the original
            // "no carry-over, re-pick" behavior was a real regression
            // once it actually happened live) — fired by
            // `mlb_engine.reconcile_superseded_mlb_series_picks` the
            // instant a projected matchup's REAL series shows up in
            // the vendor feed. The pick now carries over automatically
            // (same 2 teams, just a new series id) — just let the
            // player know it's official now.
            if (msg.event === "mlb_series_pick_migrated") {
                const p = msg.payload || {};
                const myId = sessionData?.participant_id;
                if (myId && p.participant_id === myId) {
                    const n = (p.series_ids || []).length;
                    toast.success(`Your Series Predictor pick${n > 1 ? "s" : ""} went live!`, {
                        description: "The real matchup is out now — your pick carried over automatically.",
                        duration: 8000,
                    });
                    load();
                }
            }
            if (msg.event === "round_started") {
                toast.message(`🏟️ ${msg.payload?.stage?.toUpperCase()} has started!`, {
                    description: "Leaderboard snapshot taken — round movers will start tracking.",
                });
                load();
            }
            if (msg.event === "espn_sync") {
                if ((msg.payload?.imported || 0) > 0) {
                    toast.success(`ESPN sync: ${msg.payload.imported} new match${msg.payload.imported > 1 ? "es" : ""}`);
                }
                load();
            }
            if (msg.event === "scoring_updated") {
                toast.message("⚖️ Scoring rules updated", { description: "The admin changed point values.", duration: 6000 });
                api.listLeagueChanges(leagueId, 5).then(setRecentChanges).catch(() => {});
                load();
            }
            if (msg.event === "draft_cleared") {
                toast.error("Draft cleared by the admin", { description: "Your drafted teams have been reset.", duration: 8000 });
                api.listLeagueChanges(leagueId, 5).then(setRecentChanges).catch(() => {});
                load();
            }
            if (msg.event === "admin_participation_changed") {
                api.listLeagueChanges(leagueId, 5).then(setRecentChanges).catch(() => {});
            }
            if (msg.event === "match_corrected" || msg.event === "match_deleted") {
                api.listLeagueChanges(leagueId, 5).then(setRecentChanges).catch(() => {});
                load();
            }
            // iter45 (M3) — refresh switcher when tournament list/state changes
            if (
                msg.event === "tournament_created"
                || msg.event === "tournament_completed"
                || msg.event === "active_tournament_changed"
                || msg.event === "tournament_reopened"
            ) {
                setSwitcherRefreshKey((k) => k + 1);
                load();
            }
            // iter63z — Refresh dashboard when per-tournament settings flip
            // (e.g. 3rd-place playoff toggle) so the Scoring modal shows the
            // current state without needing to navigate away.
            if (msg.event === "tournament_settings_updated") {
                load();
            }
            // iter46 (M4) — Archive lifecycle notifications.
            if (msg.event === "tournament_archive_warning") {
                const p = msg.payload || {};
                toast.warning(
                    `⏳ "${p.tournament_name || "Tournament"}" will auto-archive in ${p.days_until_delete ?? 7} day${p.days_until_delete === 1 ? "" : "s"}`,
                    {
                        description: "Free leagues clear completed tournaments after 45 days. Upgrade or ask the site admin to keep it longer.",
                        duration: 12000,
                    }
                );
            }
            if (msg.event === "tournament_archived") {
                const p = msg.payload || {};
                toast.error(
                    `📦 "${p.tournament_name || "Tournament"}" was auto-archived`,
                    { description: "Its results have been cleared. Contact a site admin to restore.", duration: 12000 }
                );
                setSwitcherRefreshKey((k) => k + 1);
                load();
            }
        });
        // Polling fallback at 45s. Auto-stop after 5 consecutive failures so we don't
        // hammer a dead endpoint forever — user can refresh to retry.
        const interval = setInterval(() => {
            if (loadFailCountRef.current < 5) load();
        }, 45000);
        return () => { sock.close(); clearInterval(interval); };
        // iter63z'''''''' — Depend on `sessionData?.participant_id` (a
        // primitive) not the whole `sessionData` object. `session.load()`
        // returns a fresh object on every call, which would otherwise
        // re-trigger this effect on every render and thrash the WS
        // subscription. The original inline code had the same latent
        // issue — fixing it as part of the hook extraction.
    }, [leagueId, load, navigate, sessionData?.participant_id, hasSession]);

    return {
        // session/auth context
        sessionData,
        hasSession,
        // payload + derived
        data,
        setData,
        board,
        setBoard,
        me,
        setMe,
        isSpectator,
        roundMovers,
        recentChanges,
        setRecentChanges,
        seasonStanding,
        wsConnected,
        // smack
        smackPing,
        unreadMentions,
        setUnreadMentions,
        // tournament switcher
        viewTournamentId,
        setViewTournamentId,
        switcherRefreshKey,
        tournamentCount,
        setTournamentCount,
        // imperative
        load,
        // Reset the 4s load throttle so the very NEXT call goes through.
        // Used by the TournamentSwitcher onChange so swapping tournaments
        // fetches fresh data immediately even if a poll just fired.
        resetLoadThrottle: () => { lastLoadAtRef.current = 0; },
    };
}
